import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertBody, AlertDescription, AlertIcon, AlertTitle } from '../../../design-system/alert';
import { GroupType } from '../../../entities/groups';
import { ClaimLimitPeriod } from '../../../entities/incentives';
import { useCurrentCompany } from '../../../lib/who-am-i';
import { useUserCountMetadata } from '../../../queries/users';

type BudgetEstimatePeriodProps = {
	amount: number;
	conditions: { attr: string | undefined; value: string | undefined }[];
	operator: GroupType;
	period: ClaimLimitPeriod;
	quantity: number | null;
	userClaimLimit: number | null;
};

export const BudgetEstimatePeriod: FC<BudgetEstimatePeriodProps> = ({ amount, conditions, operator, period, quantity, userClaimLimit }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'form' });
	const company = useCurrentCompany();

	const filteredConditions = useMemo(() => {
		return conditions.filter((condition) => condition.attr && condition.value) as { attr: string; value: string }[];
	}, [conditions]);

	const { data } = useUserCountMetadata({ conditions: filteredConditions, operator, status: 'active' });

	if (!data || !amount || !quantity) {
		return null;
	}

	const totalQuantity = Math.min(quantity, data.scopedCount * (typeof userClaimLimit === 'number' && !isNaN(userClaimLimit) ? userClaimLimit : Infinity));
	const totalPoints = totalQuantity * amount;
	const totalDollars = totalPoints / company.budget.exchange_rate;

	return (
		<Alert status="info" size="sm">
			<AlertIcon />
			<AlertBody>
				<AlertTitle>{t('estimateTitle', { amount: totalDollars, context: period })}</AlertTitle>
				<AlertDescription>
					{t('estimatePerPeriod', { count: totalQuantity, context: period })} {t('estimateTotal', { count: totalPoints, context: period })}
				</AlertDescription>
			</AlertBody>
		</Alert>
	);
};
